import React, { useCallback, useEffect } from 'react';
import { BlurView } from 'expo-blur';
import { GlassView, isLiquidGlassAvailable } from 'expo-glass-effect';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  interpolate,
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, Image, theme } from '@sg/garnish';

import { NotificationIcon } from '../../assets';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A decorative mock of an OS notification, used inside permission primers to
 * illustrate what the customer will receive once they grant the permission.
 */
export const PermissionsPromptNotificationCard = (props: Props) => {
  const { title, time, isPulsing, isMuted } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerStyles = [
    styles.contentContainer,
    isMuted ? styles.mutedContentContainer : undefined,
  ];

  const titleStyles = [styles.title, isMuted ? styles.mutedTitle : undefined];
  const timeStyles = [styles.time, isMuted ? styles.mutedTime : undefined];
  const iconStyles = [styles.icon, isMuted ? styles.mutedIcon : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <NotificationSurface />

      <View style={contentContainerStyles}>
        <View style={styles.iconContainer}>
          <Image
            source={NotificationIcon}
            style={iconStyles}
            contentFit="contain"
            accessible={false}
          />

          {isPulsing ? <NotificationPulse /> : null}
        </View>

        <View style={styles.headingContainer}>
          <BodyText bold sizeMatch={['16']} style={titleStyles}>
            {title}
          </BodyText>

          <BodyText sizeMatch={['14']} style={timeStyles}>
            {time}
          </BodyText>
        </View>
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The card's frosted backdrop. Uses the system Liquid Glass material where the
 * OS provides it, and falls back to a blur everywhere else.
 */
const NotificationSurface = () => {
  if (IS_LIQUID_GLASS_AVAILABLE) {
    return <GlassView style={styles.blur} glassEffectStyle="clear" />;
  }

  return <BlurView style={styles.blur} intensity={BLUR_INTENSITY} tint="light" />;
};

const NotificationPulse = () => {
  const progressSV = useSharedValue(0);

  // ─── Handlers ────────────────────────────────────────────────────────

  const startAnimation = useCallback(() => {
    progressSV.value = withRepeat(
      withTiming(1, { duration: PULSE_DURATION, easing: Easing.linear }),
      -1,
    );
  }, [progressSV]);

  const stopAnimation = useCallback(() => {
    progressSV.value = 0;
  }, [progressSV]);

  const triggerAnimation = useCallback(() => {
    startAnimation();

    return stopAnimation;
  }, [startAnimation, stopAnimation]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(triggerAnimation, [triggerAnimation]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.pulseContainer} pointerEvents="none" accessible={false}>
      {PULSE_RINGS.map((ring, index) => (
        <PulseRing
          key={`pulse-ring-${index}`}
          size={ring.size}
          phaseOffset={ring.phaseOffset}
          progressSV={progressSV}
        />
      ))}

      <View style={styles.pulseDot} />
    </View>
  );
};

const PulseRing = (params: PulseRingParams) => {
  const { size, phaseOffset, progressSV } = params;

  // ─── Derived ─────────────────────────────────────────────────────────

  const phaseSV = useDerivedValue(() => (progressSV.value + phaseOffset) % 1);

  // ─── Styles ──────────────────────────────────────────────────────────

  const ringDynamicStyles = useStyle(
    () => ({ width: size, height: size, borderRadius: size / 2 }),
    [size],
  );

  const ringAnimatedStyles = useAnimatedStyle(() => ({
    transform: [{ scale: interpolate(phaseSV.value, RING_SCALE.INPUT, RING_SCALE.OUTPUT) }],
    opacity: interpolate(phaseSV.value, RING_OPACITY.INPUT, RING_OPACITY.OUTPUT),
  }));

  const ringStyles = [styles.ring, ringDynamicStyles, ringAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return <Animated.View style={ringStyles} />;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IS_LIQUID_GLASS_AVAILABLE = isLiquidGlassAvailable();

const BLUR_INTENSITY = 20;
const MUTED_ICON_OPACITY = 0.9;
const ICON_SIZE = 48;

const PULSE_AREA_SIZE = 40;
const PULSE_CORNER_OFFSET = -16;
const PULSE_DOT_SIZE = 12;
const PULSE_DURATION = 3600;

const PULSE_RING_COUNT = 4;
const PULSE_RING_SIZE_STEP = 4;

const RING_SCALE = { INPUT: [0, 1], OUTPUT: [1, 1.9] };
const RING_OPACITY = { INPUT: [0, 0.12, 0.7, 1], OUTPUT: [0, 1, 0.4, 0] };

const PULSE_RINGS = Array.from({ length: PULSE_RING_COUNT }, (_unused, index) => ({
  size: PULSE_DOT_SIZE + index * PULSE_RING_SIZE_STEP,
  phaseOffset: index / PULSE_RING_COUNT,
}));

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.xlarge,
    boxShadow: '0 7.2px 36px 0 rgba(0, 0, 0, 0.1)',
  },
  blur: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.xlarge,
    overflow: 'hidden',
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['3'],
    padding: theme.spacing['4'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.WHITE.ALMOST_TRANSPARENT,
  },
  iconContainer: {
    width: ICON_SIZE,
    height: ICON_SIZE,
  },
  icon: {
    width: '100%',
    height: '100%',
  },
  mutedIcon: {
    opacity: MUTED_ICON_OPACITY,
  },
  pulseContainer: {
    position: 'absolute',
    top: PULSE_CORNER_OFFSET,
    right: PULSE_CORNER_OFFSET,
    width: PULSE_AREA_SIZE,
    height: PULSE_AREA_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: theme.colors.LIME,
  },
  pulseDot: {
    width: PULSE_DOT_SIZE,
    height: PULSE_DOT_SIZE,
    borderRadius: PULSE_DOT_SIZE / 2,
    backgroundColor: theme.colors.LIME,
  },
  mutedContentContainer: {
    backgroundColor: theme.colors.OPACITY.WHITE.TRANSPARENT,
  },
  headingContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['2'],
  },
  title: {
    flexShrink: 1,
    color: theme.colors.WHITE,
  },
  mutedTitle: {
    color: theme.colors.OPACITY.WHITE.DARKER,
  },
  time: {
    color: theme.colors.OPACITY.WHITE.DARKER,
  },
  mutedTime: {
    color: theme.colors.OPACITY.WHITE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  title: string;
  time: string;
  isPulsing?: boolean;
  isMuted?: boolean;
};

type PulseRingParams = {
  size: number;
  phaseOffset: number;
  progressSV: SharedValue<number>;
};
